// offload.ts — one capture's server-mode hand-off (ARCHITECTURE.md §15.3),
// kept out of session.ts so the orchestrator stays small. Given the decision,
// the running recorder and the lens prior, begin() POSTs video/start, routes
// the recorder's backlog and every later chunk into the uploader, and replays
// the IMU samples recorded before the switch; pushImu() streams new ones;
// finish() stops the recorder (its final chunk lands first), waits for every
// chunk and POSTs video/finish {last_seq}. A finish whose last POST failed
// can be retried while every chunk is still accounted for.
//
// With OffloadInput.live the video/start body asks for live mode (§15.7):
// `live: true`, left out otherwise so the request is unchanged for servers
// and captures that do not use it.
//
// It also keeps the pre-switch IMU log (ImuLog): device mode feeds samples to
// the WASM filter AND records them on the same performance.now() clock, so a
// switch hands the server the whole trace (§15.2).

import type { Scan, VideoStartRequest } from '../api';
import type { OffloadDecision } from './perf';
import type { PriorConfig } from './profile';
import type { VideoRecorder } from './recorder';
import { VideoUploader } from './upload';
import type { UploadApi, UploadStats } from './upload';
import type { ImuSampleJSON } from './worker';

// IMU_LOG_CAP bounds the pre-switch trace (~25 min at 100 Hz).
export const IMU_LOG_CAP = 150_000;

// ImuLog records device-mode IMU samples (oldest dropped past the cap).
export class ImuLog {
  private samples: ImuSampleJSON[] = [];
  dropped = 0;
  angle = 0;

  constructor(readonly cap = IMU_LOG_CAP) {}

  get length(): number {
    return this.samples.length;
  }

  add(batch: readonly ImuSampleJSON[], screenAngle: number): void {
    for (const s of batch) this.samples.push(s);
    this.angle = screenAngle;
    const over = this.samples.length - this.cap;
    if (over > 0) {
      this.samples.splice(0, over);
      this.dropped += over;
    }
  }

  drain(): ImuSampleJSON[] {
    const out = this.samples;
    this.samples = [];
    return out;
  }
}

export interface OffloadInput {
  scanId: string;
  decision: OffloadDecision;
  recorder: VideoRecorder;
  width: number; // recorded video size
  height: number;
  deviceKey?: string;
  prior: PriorConfig | null;
  screenAngle: number;
  live?: boolean; // ask for live mode (§15.7): GPU splat training while the video streams
}

// videoStartBody builds the §15.3 video/start payload.
export function videoStartBody(o: OffloadInput, recorderStartMs: number): VideoStartRequest {
  return {
    mime: o.recorder.mime,
    recorder_start_ms: recorderStartMs,
    camera_fps: o.decision.camera_fps,
    processing_fps: o.decision.processing_fps,
    reason: o.decision.reason,
    width: o.width,
    height: o.height,
    ...(o.deviceKey ? { device_key: o.deviceKey } : {}),
    ...(o.prior ? { prior_intrinsics: o.prior.prior_intrinsics, prior_sigma: o.prior.prior_sigma } : {}),
    screen_angle: o.screenAngle,
    ...(o.recorder.truncated ? { video_truncated: true } : {}),
    ...(o.live ? { live: true } : {}),
  };
}

export interface OffloadCallbacks {
  onStats?: (s: UploadStats) => void;
  onError?: (message: string) => void;
}

export class ServerOffload {
  readonly uploader: VideoUploader;
  private began: Promise<void> | null = null;
  private attached = false;
  private recorderStopped = false;

  constructor(private input: OffloadInput, api: UploadApi, cb: OffloadCallbacks = {}) {
    this.uploader = new VideoUploader(input.scanId, api, { onStats: cb.onStats, onError: cb.onError });
  }

  get snapshot(): UploadStats {
    return this.uploader.snapshot;
  }

  // begin attaches the recorder (backlog first), replays the IMU log and
  // POSTs video/start. Chunks queue in the uploader until the POST returns;
  // a failed POST is tried again by finish().
  begin(imuBacklog: readonly ImuSampleJSON[], imuAngle: number): Promise<void> {
    if (this.attached) return this.post();
    this.attached = true;
    const rec = this.input.recorder;
    rec.attach((c) => this.uploader.addChunk(c));
    this.uploader.addImu(imuBacklog, imuAngle);
    console.info(`[openscan/offload] handing scan ${this.input.scanId} to the server: ${this.input.decision.reason}; ${rec.chunkCount} chunks recorded, ${imuBacklog.length} IMU samples logged`);
    return this.post();
  }

  private post(): Promise<void> {
    if (this.began) return this.began;
    const rec = this.input.recorder;
    const startMs = rec.startMs ?? performance.now();
    if (rec.startMs === null) console.warn('[openscan/offload] recorder onstart has not fired; using now as recorder_start_ms');
    const p = this.uploader.begin(videoStartBody(this.input, startMs)).then(() => undefined);
    this.began = p;
    p.catch(() => {
      if (this.began === p) this.began = null; // let finish() try again
    });
    return p;
  }

  pushImu(samples: readonly ImuSampleJSON[], screenAngle: number): void {
    this.uploader.addImu(samples, screenAngle);
  }

  // finish stops the recorder, uploads the rest and POSTs video/finish.
  async finish(): Promise<Scan | null> {
    if (!this.attached) throw new Error('server processing never started');
    await this.post();
    if (!this.recorderStopped) {
      await this.input.recorder.stop();
      this.recorderStopped = true;
    }
    return this.uploader.finish(this.input.recorder.lastSeq);
  }

  // retryable: every chunk is still accounted for, so finish() may run again.
  get retryable(): boolean {
    return this.attached && this.uploader.healthy;
  }

  close(): void {
    this.uploader.close();
    this.input.recorder.discard();
  }
}
